import { AppLayout } from '@/components/AppLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useState, useEffect } from 'react';
import { Mail, User, Lock, KeyRound, Phone, Building2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DeleteAccountSection } from '@/components/DeleteAccountSection';
import { ConsentHistorySection } from '@/components/consent/ConsentHistorySection';
import { useTranslation } from 'react-i18next';
import {
  getCanonicalPortalPasswordStatus,
  protectedPasswordLogin,
  resolvePasswordReauthenticationVisibleEmail,
} from '@/lib/protectedPublicAuth';
import { initiateGovernedEmailChange } from '@/lib/governedEmailChange';

export default function MyAccount() {
  const { t } = useTranslation();
  const { user, role } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch hire profile details
  const { data: hireProfile, isLoading } = useQuery({
    queryKey: ['my-hire-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hire_profiles')
        .select('id, full_name, email, department, avatar_url')
        .eq('user_id', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: hasForkPassword } = useQuery({
    queryKey: ['canonical-portal-password-status', 'hire', user?.id],
    queryFn: async () => {
      const result = await getCanonicalPortalPasswordStatus(supabase, 'hire');
      if (result.error || result.hasPassword === null) throw result.error;
      return result.hasPassword;
    },
    enabled: !!user?.id,
  });

  // Editable fields
  const [fullName, setFullName] = useState('');
  const [department, setDepartment] = useState('');
  const [origFullName, setOrigFullName] = useState('');
  const [origDepartment, setOrigDepartment] = useState('');

  // Login email
  const [loginEmail, setLoginEmail] = useState('');
  const [origLoginEmail, setOrigLoginEmail] = useState('');

  // Password dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [dialogPurpose, setDialogPurpose] = useState<'profile' | 'email'>('profile');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (hireProfile) {
      setFullName(hireProfile.full_name || '');
      setDepartment(hireProfile.department || '');
      setOrigFullName(hireProfile.full_name || '');
      setOrigDepartment(hireProfile.department || '');
    }
  }, [hireProfile]);

  useEffect(() => {
    if (user?.email) {
      setLoginEmail(user.email);
      setOrigLoginEmail(user.email);
    }
  }, [user]);

  const hasProfileChanges = fullName !== origFullName || department !== origDepartment;
  const hasEmailChange = loginEmail !== origLoginEmail;

  const updateProfile = useMutation({
    mutationFn: async (updates: { full_name?: string; department?: string }) => {
      const { error } = await supabase
        .from('hire_profiles')
        .update(updates)
        .eq('user_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-hire-profile'] });
      toast({ title: t('hire.account.profileUpdated'), description: t('hire.account.profileUpdatedDesc') });
    },
    onError: () => {
      toast({ title: t('hire.account.updateFailed'), description: t('hire.account.updateFailedDesc'), variant: 'destructive' });
    },
  });

  const verifyPassword = async (): Promise<boolean> => {
    if (!confirmPassword.trim()) {
      toast({ title: t('hire.settings.passwordRequired'), description: t('hire.settings.passwordRequiredDesc'), variant: 'destructive' });
      return false;
    }
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const visibleEmail = currentUser ? resolvePasswordReauthenticationVisibleEmail(currentUser) : '';
    if (!currentUser?.email || !visibleEmail) {
      toast({ title: t('hire.account.error'), description: t('hire.account.verifyIdentityFailed'), variant: 'destructive' });
      return false;
    }
    const { error } = await protectedPasswordLogin(supabase, {
      portal: 'hire',
      email: currentUser.email,
      visibleEmail,
      password: confirmPassword,
    });
    if (error) {
      toast({ title: t('hire.settings.incorrectPassword'), description: t('hire.settings.incorrectPasswordDesc'), variant: 'destructive' });
      return false;
    }
    return true;
  };

  const handleSaveProfile = () => {
    if (!hasProfileChanges) return;
    setConfirmPassword('');
    setDialogPurpose('profile');
    setShowPasswordDialog(true);
  };

  const handleSaveEmail = () => {
    if (!hasEmailChange) return;
    if (!loginEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
      toast({ title: t('hire.settings.invalidEmail'), description: t('hire.settings.invalidEmailDesc'), variant: 'destructive' });
      return;
    }
    setConfirmPassword('');
    setDialogPurpose('email');
    setShowPasswordDialog(true);
  };

  const handleConfirmSave = async () => {
    setVerifying(true);
    const verified = await verifyPassword();
    if (!verified) { setVerifying(false); return; }

    if (dialogPurpose === 'email') {
      const { error } = await initiateGovernedEmailChange(supabase, {
        portal: 'hire',
        newEmail: loginEmail,
        confirmPassword,
      });
      if (error) {
        toast({ title: t('hire.settings.emailUpdateFailed'), description: error.message, variant: 'destructive' });
      } else {
        toast({ title: t('hire.settings.confirmationSent'), description: t('hire.account.confirmationSentDesc') });
        setOrigLoginEmail(loginEmail);
        setShowPasswordDialog(false);
        setConfirmPassword('');
      }
      setVerifying(false);
    } else {
      updateProfile.mutate(
        { full_name: fullName, department },
        {
          onSuccess: () => {
            setOrigFullName(fullName);
            setOrigDepartment(department);
            setShowPasswordDialog(false);
            setConfirmPassword('');
          },
          onSettled: () => setVerifying(false),
        }
      );
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6 max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display text-foreground">{t('hire.account.title')}</h1>
          <p className="text-muted-foreground mt-2 text-sm">{t('hire.account.subtitle')}</p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Personal Info */}
            <Card className="p-6 sm:p-8 shadow-card border-border space-y-5 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-5 h-5 text-accent" />
                <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.account.personalInfo')}</h2>
              </div>
              <p className="text-xs text-muted-foreground -mt-3">
                {t('hire.account.personalInfoDesc')}
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.account.fullName')}
                  </label>
                  <Input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-11 rounded-xl"
                    placeholder={t('hire.account.fullNamePlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.settings.loginEmail')}
                  </label>
                  <Input
                    value={hireProfile?.email || ''}
                    disabled
                    className="h-11 rounded-xl bg-muted/50 cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">{t('hire.account.profileEmailHint')}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.team.usersList.columns.department')}
                  </label>
                  <Input
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    className="h-11 rounded-xl"
                    placeholder={t('hire.account.departmentPlaceholder')}
                  />
                </div>

                {role && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">{t('hire.team.usersList.columns.role')}</label>
                    <Input
                      value={role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      disabled
                      className="h-11 rounded-xl bg-muted/50 cursor-not-allowed capitalize"
                    />
                    <p className="text-xs text-muted-foreground">{t('hire.account.roleManagedHint')}</p>
                  </div>
                )}
              </div>

              <Button
                className="gradient-accent text-accent-foreground hover:opacity-90 touch-target h-10 px-5 rounded-xl"
                onClick={handleSaveProfile}
                disabled={!hasProfileChanges || updateProfile.isPending}
              >
                {updateProfile.isPending ? t('hire.account.saving') : t('hire.account.saveChanges')}
              </Button>
            </Card>

            {/* Login & Security */}
            <Card className="p-6 sm:p-8 shadow-card border-border space-y-5 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-5 h-5 text-accent" />
                <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.settings.loginSecurity')}</h2>
              </div>
              <p className="text-xs text-muted-foreground -mt-3">
                {t('hire.settings.loginSecurityDesc')}
              </p>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.settings.loginEmail')}
                </label>
                <Input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="h-11 rounded-xl"
                />
                <p className="text-xs text-muted-foreground">{t('hire.settings.loginEmailHint')}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  className="gradient-accent text-accent-foreground hover:opacity-90 touch-target h-10 px-5 rounded-xl"
                  onClick={handleSaveEmail}
                  disabled={!hasEmailChange}
                >
                  {t('hire.settings.updateEmail')}
                </Button>
                <Button
                  variant="outline"
                  className="touch-target h-10 px-5 rounded-xl gap-1.5"
                  onClick={() => navigate('/change-password')}
                >
                  <KeyRound className="w-4 h-4" />
                  {t(hasForkPassword === false ? 'hire.settings.setForkPassword' : 'hire.settings.changePassword')}
                </Button>
              </div>
              {hasForkPassword === false ? (
                <p className="text-xs text-muted-foreground">{t('hire.settings.setForkPasswordDesc')}</p>
              ) : null}
            </Card>

            {/* Consents & Acknowledgements */}
            <ConsentHistorySection userId={user?.id} portal="hire" />

            {/* Delete Account */}
            <DeleteAccountSection
              portal="hire"
              onDelete={async () => {
                const { data, error } = await supabase.functions.invoke('delete-portal-account', {
                  body: { portal: 'hire' },
                });
                if (error) throw error;
                if (data?.error) throw new Error(data.error);
              }}
              onSignOut={() => supabase.auth.signOut().then(() => navigate('/hire/auth'))}
            />

            {/* Privacy link */}
            <div className="text-center pb-4">
              <Link
                to="/privacy"
                state={{ from: '/my-account' }}
                className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
              >
                {t('hire.account.privacyPolicy')}
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Password Confirmation Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">{t('hire.settings.confirmIdentity')}</DialogTitle>
            <DialogDescription>
              {dialogPurpose === 'email'
                ? t('hire.settings.confirmEmailChange')
                : t('hire.account.confirmProfileChange')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm font-medium text-foreground">{t('hire.settings.password')}</label>
            <div className="relative">
              <Lock className="absolute start-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                className="ps-10 h-11 rounded-xl"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSave(); }}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setShowPasswordDialog(false)} disabled={verifying}>
              {t('common.cancel')}
            </Button>
            <Button className="gradient-accent text-accent-foreground hover:opacity-90 rounded-xl" onClick={handleConfirmSave} disabled={verifying}>
              {verifying ? t('hire.account.verifying') : t('hire.account.confirmAndSave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
